// FP 주문 간트 차트 — **프레임 자체 구현(AG Grid 미사용)**
//
// 전환 이유와 손익은 reports/gantt-implementation.md §7-1에 있다. 요지는 좌표 소유권이다 —
// 초기 AG Grid 버전은 그리드가 그린 행/바의 DOM 사각형을 오버레이가 다시 읽어야 해서 스크롤·
// 펼침·애니메이션마다 의존선이 어긋났다(§5-1a B4·B10·B13~B16). 여기서는 바를 우리가 그리고 그
// 좌표를 그대로 의존선에 쓰므로 그 부류의 결함이 구조적으로 생기지 않는다.
// 병행 유지했던 AG Grid 버전은 2026-08-19에 제거하고 이 파일이 정식 이름을 가져갔다(§7-3).
//
// 구성: 조회조건(Report 공통) + `GanttFrame` + `ganttDraw` + `ganttRowModel`(트리 표시행 계산).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpOrderGanttService from "@plannel/services/fp/fp-order-gantt-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import GanttFrame from "@plannel/pages/factory-plan/common/gantt/GanttFrame";
import GanttTooltip from "@plannel/pages/factory-plan/common/gantt/GanttTooltip";
import GanttViewControls from "@plannel/pages/factory-plan/common/gantt/GanttViewControls";
import { buildRowLayout, descendantKeys, visibleTreeRows } from "@plannel/pages/factory-plan/common/gantt/ganttRowModel";
import {
  drawBox,
  drawDependency,
  drawOrderBox,
  drawRowDivider,
  drawSelectionRing,
  drawTimelineGrid,
  drawWeekends
} from "@plannel/pages/factory-plan/common/gantt/ganttDraw";
import { buildGanttBound, buildTicks, buildTimeScale, buildWeekendRanges, toMs } from "@plannel/pages/factory-plan/common/gantt/timelineScales";
import { formatCodeName, formatQty, formatTimeDiff } from "@plannel/pages/factory-plan/common/gantt/ganttFormat";
import {
  DEFAULT_HEADER_UNIT,
  LANE_BAR_PADDING_Y,
  ROW_HEIGHT_DEFAULT,
  WIDTH_ZOOM_MAX,
  WIDTH_ZOOM_MIN,
  WIDTH_ZOOM_STEP
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 다른 Report 화면과 동일 결정(§A-3c).
const SEARCH_ZONE_DAYS = 9;

// old SearchArea의 입력 2개(woCd / item) — OrderGanttService가 소비하는 파라미터명은 woCd / itemCdNm.
const SEARCH_FIELD_LABELS = { woCd: "FP_WO_CD", itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";
const ORDER_LEVEL = 0;   // 트리 최상위 = 작업주문(old row.Level === 0)

// 이 화면에는 **휴무(비가동)·동결(freeze) 배경을 그리지 않는다** — old와 같다(2026-08-24 사용자 확정).
// old `OrderGantt.jsx`에는 `setFrozenZone` 호출도, `GanttBackground`(`#11` 동결 / `#20` 비가동) 설정도
// 없다(확인함). 둘 다 **자원 축 정보**라서다 — 비가동은 자원 캘린더이고, 이 화면의 행은 자원이 아니라
// 작업 주문이다. 자원 간트(`ResourceGantt.js`)에만 있다.
// 2026-08-19에 `drawHolidays`용 빈 배열 + TODO(MIG-T2)를 둬 뒀던 것은 이 결정으로 **철회**한다.
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// 행 키 구분자 — 화면 데이터에 나올 수 없는 제어문자를 쓴다(경로 접두 비교로 하위 행을 찾는다).
//
// ⚠️ 화살표 끝점을 `id`(= activityId)로 찾으면 안 된다 — `tb_fp_activity`의 UK가
// `(plan_scope, version_cd, plant_cd, stage_cd, activity_id, resource_cd)`라 **activity_id는 전역
// 유일이 아니다**(2026-08-18 B13). 경로로 만든 `rowKey`(형제 인덱스 포함)를 끝점으로 쓴다.
const ROW_KEY_SEP = String.fromCharCode(1);

// 백엔드가 각 노드의 자식을 `items`에 재귀적으로 담아 내려준다(OrderGanttService#makeTree).
// 이 중첩 구조를 평탄화하면서 **의존선(작업 순서 화살표)도 같이 뽑는다** — 트리의 자식이 곧 선행
// 공정(백엔드 parent = nextActivityId)이라 별도 `anc` 파싱이 필요 없다.
//   ※ 필드명은 2026-08-26에 `Items` → `items`로 바뀌었다. old는 TreeGrid가 트리 행 구성 필드명을
//     대소문자까지 `Items`로 요구해서 백엔드가 `@JsonProperty("Items")`로 맞춰 보냈지만, new는
//     여기서 직접 평탄화하므로 그 제약이 없다. 대문자 필드명은 Jackson에서 프로퍼티가 둘로 갈려
//     응답이 2^깊이로 폭발하는 원인이 됐다(gantt-implementation.md §7-16).
const flattenGanttTree = (nodes, parentPath = [], acc = { rows: [], edges: [] }) => {
  (nodes ?? []).forEach((node, index) => {
    const path = [...parentPath, `${node.id ?? node.tree ?? index}__${index}`];
    const rowKey = path.join(ROW_KEY_SEP);
    const isOrderRow = node.id != null && node.id === node.woCd;

    acc.rows.push({
      rowKey,
      path,
      level: path.length - 1,
      isOrderRow,
      id: node.id ?? node.tree,
      mainCol: node.mainCol,
      woCd: node.woCd,
      plantCd: node.plantCd,
      plantNm: node.plantNm,
      itemCd: node.itemCd,
      itemNm: node.itemNm,
      itemUom: node.itemUom,
      qty: node.qty,
      resourceCd: node.resourceCd,
      activityId: node.activityId,
      startTs: node.startTs,
      endTs: node.endTs,
      dueDate: node.dueDate,
      displayColor: node.displayColor,
      lateYn: node.lateYn
    });

    (node.items ?? []).forEach((child, childIndex) => {
      const childKey = [...path, `${child.id ?? child.tree ?? childIndex}__${childIndex}`].join(ROW_KEY_SEP);

      // **주문(Level 0) 행으로 들어오는 화살표는 만들지 않는다** — 후속 공정이 없는 작업의 `parent`가
      // `woCd`로 폴백돼 트리상 주문의 자식이 된 것뿐이고 공정 순서가 아니다(B11).
      if (!isOrderRow) acc.edges.push({ from: childKey, to: rowKey });
    });

    if (node.items?.length) flattenGanttTree(node.items, path, acc);
  });

  return acc;
};

// 행 1개 = 바 1개. 자원 간트와 같은 렌더 프리미티브를 쓰기 위해 `boxes` 형태로 맞춘다.
const toBox = (row) => {
  const s = toMs(row.startTs);
  const e = toMs(row.endTs);
  if (Number.isNaN(s)) return null;

  return {
    id: row.rowKey,
    s,
    e: Number.isNaN(e) ? s : e,
    type: "box",
    color: row.displayColor,
    text: row.woCd,
    late: row.lateYn === true || row.lateYn === "true",
    hasTool: false
  };
};

const OrderGantt = ({ t, viewName }) => {
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const woCdRef = useRef("");
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [edges, setEdges] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [bound, setBound] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [hoverRowKey, setHoverRowKey] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const [headerUnit, setHeaderUnit] = useState(DEFAULT_HEADER_UNIT);
  const [widthZoom, setWidthZoom] = useState(1);
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT_DEFAULT);

  const frameApiRef = useRef(null);
  // rowKey → 마지막으로 그린 바의 화면 좌표. 의존선이 좌표를 되찾는 통로.
  const boxRectsRef = useRef(new Map());
  // 선택된 바가 **실제로 그려진 정수 사각형** — 행 렌더에서 채우고 overlay에서 링을 그린다.
  // `geom`(소수)을 링에서 다시 반올림하면 바와 1px 어긋난다(§7-9) → 바가 반환한 값을 그대로 쓴다.
  const selectedRectRef = useRef(null);
  const hoverTimerRef = useRef(0);
  const tooltipTokenRef = useRef(0);
  const latestReqRef = useRef(0);

  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const weekdayLabels = useMemo(() => WEEKDAY_KEYS.map(key => t(key)), [t]);

  // ── 시간축 / 표시 행 ──────────────────────────────────────────────────────
  const scale = useMemo(
    () => (bound ? buildTimeScale({ startTs: bound.startTs, endTs: bound.endTs, unit: headerUnit, zoom: widthZoom }) : null),
    [bound, headerUnit, widthZoom]
  );
  const ticks = useMemo(() => buildTicks(scale, { weekdayLabels }), [scale, weekdayLabels]);
  // 주말 밴드는 축(t0/t1)만 바뀔 때 다시 계산하면 된다 — 버킷·줌이 바뀌면 scale이 새로 만들어진다.
  const weekendRanges = useMemo(() => buildWeekendRanges(scale), [scale]);

  // 펼쳐진 조상만 가진 행 → 행 높이/누적 top 계산. 주문 간트는 행당 바가 1개라 겹침 트랙이 없다.
  const layout = useMemo(() => {
    const visible = visibleTreeRows(allRows, expandedKeys, ROW_KEY_SEP)
      .map(row => ({ ...row, boxes: [toBox(row)].filter(Boolean) }));
    return buildRowLayout(visible, { baseRowHeight: rowHeight });
  }, [allRows, expandedKeys, rowHeight]);

  // rowKey → 표시 행(top/height 포함). 의존선이 **스크롤로 화면을 벗어난** 행의 끝점 좌표를 계산할 때
  // 쓴다(§7-12). **접힌 행은 여기 없다** — `layout.rows`가 곧 표시 행 목록이므로, 접힌 행으로 향하는
  // 화살표는 자연히 그려지지 않는다(B11·B12의 유령 화살표 방지 규칙이 그대로 유지된다).
  const rowIndex = useMemo(
    () => new Map(layout.rows.map(row => [row.rowKey, row])),
    [layout]
  );

  // TODO(MIG-T2): [ordergantt/OrderGantt] 좌측 컬럼 정렬 / 컬럼 상태 저장 미이관(폭 조절은 구현됨).
  //   old(AG Grid 버전): `sortable` + `GridUtils.setColumnDefs`의 컬럼 상태 저장/복원
  //   사유: 자체 프레임 전환으로 상실 — 간트는 행 순서가 곧 주문·공정 순서다(§7-1)
  const columns = useMemo(() => [
    // old plantNm은 세로 병합(`Spanned:'1'` + 백엔드 `plantNmRowSpan`)이라 공장당 한 번만 보였다.
    // 자체 프레임의 `spanRows`(연속 동일값 병합)로 같은 결과를 만든다 — 백엔드가 내려주는 행수
    // 대신 실제 표시 행의 연속성을 쓰므로 트리를 펼쳐도 자연히 맞는다.
    { key: "plantNm", label: t("FP_PLANT"), width: 130, spanRows: true, valueGetter: (row) => row.plantNm ?? "" },
    // 작업 주문 = 트리 컬럼(들여쓰기 + 펼침 표시). AG Grid의 autoGroupColumnDef가 항상 첫 컬럼으로
    // 들어가던 제약(§5-1a 컬럼 순서)이 없어져 원하는 위치에 그냥 둔다.
    { key: "mainCol", label: t("FP_WO_CD"), width: 320, tree: true, valueGetter: (row) => row.mainCol ?? "" },
    { key: "startTs", label: t("startDate"), width: 150, align: "center", valueGetter: (row) => dateUtils.formatDateTime(row.startTs) },
    { key: "endTs", label: t("endDate"), width: 150, align: "center", valueGetter: (row) => dateUtils.formatDateTime(row.endTs) }
  ], [t]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const closeTooltip = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    tooltipTokenRef.current += 1;
    setTooltip(null);
  }, []);

  // 바 라벨 — old OnGetGanttHtml의 `${itemCd} (${itemNm}) / ${qty} (${uom})`.
  const buildBarLabel = useCallback((row) => {
    const name = row.itemNm ? `${row.itemCd} (${row.itemNm})` : row.itemCd ?? "";
    const qty = formatQty(row.qty, row.itemUom);
    return qty ? `${name} / ${qty}` : name;
  }, []);

  // old OnGanttTip — 주문 행과 작업 행이 서로 다른 상세를 조회한다. old는 동기 XHR이라 hover마다
  // UI가 멈췄지만 new는 150ms 디바운스 + 비동기 + 요청 토큰으로 바꾼다.
  const openTooltip = useCallback((row, clientX, clientY) => {
    const version = searchStateRef.current.versionState;
    if (!version.versionCd) return;

    const token = ++tooltipTokenRef.current;
    const isOrderRow = row.level === ORDER_LEVEL;

    const request = isOrderRow
      ? fpOrderGanttService.getWoTooltip({
        planScope: version.planScope, versionCd: version.versionCd, plantCd: row.plantCd, woCd: row.woCd
      })
      : fpOrderGanttService.getBomTooltip({
        planScope: version.planScope, versionCd: version.versionCd, plantCd: row.plantCd,
        resourceCd: row.resourceCd, activityId: row.activityId
      });

    request
      .then(response => {
        if (token !== tooltipTokenRef.current) return;

        const detail = response.data;
        if (!detail) return;

        // 항목은 old와 같고 **순서·위계만** 바꾼다(정보 총량 동일, §7-3):
        //   헤더(종류 아이콘 + 식별 코드 + 상태 뱃지) / 기간 그룹 / 부가 그룹.
        // 납기일을 기간 그룹 **맨 아래**로 내린 이유: 시작·종료는 "계획 사실", 납기는 "결과 판단"이라
        // 성격이 다르고, 실데이터에서 납기가 시작보다 이전인 경우가 흔해 위에 두면 시선이 되돌아간다.
        // 지연은 **뱃지(상태) + 지연 일수 행(수치)**으로 나눈다 — 같은 정보를 두 문구로 쓰면 다른
        // 값처럼 읽힌다.
        const tip = isOrderRow
          ? {
            kind: "order",
            captionKey: "FP_WO_CD",
            code: detail.woCd,
            badge: { labelKey: detail.lateYn ? "FP_DELAY" : "FP_ONTIME", tone: detail.lateYn ? "warn" : "ok" },
            groups: [
              {
                rows: [
                  { labelKey: "FP_INVENTORY_NM", value: formatCodeName(detail.itemCd, detail.itemNm), wrap: true },
                  { labelKey: "FP_REQUEST_QTY", value: formatQty(detail.shptQty, detail.itemUom) }
                ]
              },
              {
                rows: [
                  { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.startTs) },
                  { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.endTs) },
                  { labelKey: "FP_TOTAL_PRODUCTION_TIME", value: formatTimeDiff(detail.timeDiff, t) },
                  { labelKey: "FP_DUE_DT", value: dateUtils.formatDateTime(detail.dueDate), ...(detail.lateYn ? { tone: "warn" } : null) }
                  // TODO(MIG-T2): [ordergantt/OrderGantt] 납기 초과 일수("233일 초과") 미표시.
                  //   시안(prototypes/gantt-tooltip-styles.html)에는 납기일 옆 수치가 있었다.
                  //   사유: `WoPlanDto`에 일수 필드가 없다. `latenessTime`(Long)이 있지만 단위가
                  //   확인되지 않았고(초 추정), endTs−dueDate로 프론트에서 계산하면 백엔드의 지연
                  //   판정 기준과 어긋날 수 있어 값을 만들어내지 않았다. 지금은 뱃지("지연") +
                  //   납기일 강조색까지만. 단위 확인되면 행 하나 추가로 끝난다.
                ]
              },
              {
                small: true,
                rows: [
                  { labelKey: "FP_SO_CD", value: detail.dmndCd },
                  { labelKey: "FP_SALES_ORDER_QTY", value: formatQty(detail.requestQty, detail.itemUom) },
                  { labelKey: "customer", value: detail.customerCd },
                  { labelKey: "FP_PLAN_SEQ", value: detail.planSeq == null ? "" : numberUtil.formatNumber(Number(detail.planSeq)) }
                ]
              }
            ]
          }
          : {
            kind: "activity",
            captionKey: "FP_ACTIVITY",
            // 코드와 명칭을 한 줄로 — 작업 바는 품목이 곧 식별자다.
            // ⚠️ 상세 조회는 코드와 명칭을 **각각** 내려준다(2026-08-25 백엔드 정리 — 그전에는 쿼리가
            // "코드 (명칭)"으로 합쳐 같은 alias로 내렸다). 툴팁은 한 줄 표기를 유지하므로
            // `formatCodeName`으로 합친다. `resourceCd`/`routeCd`도 같다.
            code: formatCodeName(detail.itemCd, detail.itemNm),
            groups: [
              { rows: [{ labelKey: "FP_QTY", value: formatQty(detail.qty, detail.itemUom) }] },
              {
                rows: [
                  { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.startTs) },
                  { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.endTs) },
                  { labelKey: "FP_TOTAL_WORK_TIME", value: formatTimeDiff(detail.tsDiff, t) }
                ]
              },
              {
                small: true,
                rows: [
                  { labelKey: "FP_WORK_ORDER_CODE", value: String(row.woCd ?? "").split(" ")[0] },
                  // 자원·공정은 코드+명칭을 합치면 길어질 수 있어 툴팁 폭을 늘리는 대신 두 줄을 허용한다.
                  { labelKey: "FP_RESOURCE", value: formatCodeName(detail.resourceCd, detail.resourceNm), wrap: true },
                  { labelKey: "FP_ROUTE", value: formatCodeName(detail.routeCd, detail.routeNm), wrap: true },
                  // Y/N 문자 대신 체크박스 — 상태를 글자로 읽지 않고 형태로 본다.
                  { labelKey: "FP_WIP", value: Boolean(detail.wipYn), checkbox: true }
                ]
              }
            ]
          };

        setTooltip({ x: clientX, y: clientY, ...tip });
      })
      .catch(e => debug(e));
  }, [t]);

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveOrderGantt(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const retrieveOrderGantt = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      woCd: woCdRef.current,
      itemCdNm: itemCdNmRef.current,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };

    closeTooltip();
    setSelectedRowKey(null);
    setLoading(true);

    fpOrderGanttService.getActivities(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const { rows, edges: nextEdges } = flattenGanttTree(response.data ?? []);

        setAllRows(rows);
        setEdges(nextEdges);
        setExpandedKeys(new Set());        // old expandAll 없음 — 주문 행만 보이는 상태로 시작

        // old setGanttBound() + **조회 데이터 구간 합집합** — 계획 시작보다 이른 작업이 있으면 축
        // 밖으로 밀려 바가 사라지고 의존선만 남는다(§5-1a B8).
        const dataRange = rows.reduce((acc, row) => {
          const s = toMs(row.startTs);
          const e = toMs(row.endTs);
          if (!Number.isNaN(s)) acc.start = acc.start == null ? s : Math.min(acc.start, s);
          if (!Number.isNaN(e)) acc.end = acc.end == null ? e : Math.max(acc.end, e);
          return acc;
        }, { start: null, end: null });

        setBound(buildGanttBound({
          planStartTs: version.selectedVersion?.startTs,
          planEndTs: version.selectedVersion?.endTs,
          startDate: period.startDate,
          endDate: period.endDate,
          isEntirePlanPeriod: period.isEntirePlanPeriod,
          dataStartTs: dataRange.start,
          dataEndTs: dataRange.end
        }));

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) {
          setAllRows([]);
          setEdges([]);
        }
        debug(e);
      })
      .finally(() => {
        // 늦게 온 응답이 최신 조회의 로딩 표시를 끄지 않게 한다(요청 토큰).
        if (reqId === latestReqRef.current) setLoading(false);
      });
  };

  // ── 캔버스 렌더 ───────────────────────────────────────────────────────────
  const handleDrawBackground = useCallback(({ ctx, x, width, height }) => {
    boxRectsRef.current = new Map();
    selectedRectRef.current = null;      // 선택 행이 접히거나 화면 밖이면 링도 같이 사라져야 한다
    if (!scale) return;

    // 배경은 **주말 + 격자뿐**이다 — 동결·휴무는 이 화면에서 그리지 않는다(파일 머리말 참고).
    drawWeekends(ctx, { ranges: weekendRanges, x, top: 0, height });
    drawTimelineGrid(ctx, {
      ticks: ticks.bottom, subTicks: ticks.sub,
      x0: x(scale.t0), top: 0, height, viewLeft: 0, viewRight: width
    });
  }, [scale, ticks, weekendRanges]);

  const handleDrawRow = useCallback(({ ctx, x, row, rowTop, rowHeight: rh, width }) => {
    drawRowDivider(ctx, { left: 0, width, y: rowTop + rh });

    const box = row.boxes[0];
    if (!box) return;

    const x0 = x(box.s);
    const x1 = x(box.e);
    if (x1 < -40 || x0 > width + 40) return;

    const geom = {
      left: x0,
      width: Math.max(1, x1 - x0),
      top: rowTop + LANE_BAR_PADDING_Y,
      height: Math.max(6, rh - LANE_BAR_PADDING_Y * 2)
    };

    // 주문 행은 밑변이 뾰족한 실루엣(old `.BGGanttWoBox`), 작업 행은 일반 바.
    let rect = null;
    if (row.level === ORDER_LEVEL) {
      rect = drawOrderBox(ctx, {
        geom,
        color: box.color,
        label: buildBarLabel(row),
        hovered: hoverRowKey === row.rowKey
      });
    } else {
      rect = drawBox(ctx, {
        box: { ...box, text: buildBarLabel(row) },
        geom,
        hovered: hoverRowKey === row.rowKey
      });
    }

    // 선택 링은 overlay에서 그린다 — 여기서 그리면 다음 행이 링의 아래쪽을 덮는다(§7-9).
    if (selectedRowKey && row.rowKey === selectedRowKey) selectedRectRef.current = rect;

    boxRectsRef.current.set(row.rowKey, {
      x0: geom.left, x1: geom.left + geom.width, cy: geom.top + geom.height / 2
    });
  }, [buildBarLabel, selectedRowKey, hoverRowKey]);

  // 작업 순서 화살표 — 행을 넘나들므로 행 렌더가 끝난 뒤 한 번에 그린다.
  const handleDrawOverlay = useCallback(({ ctx, x, scrollTop, height }) => {
    drawSelectionRing(ctx, { rect: selectedRectRef.current });

    // 끝점 좌표 — **그려진 사각형이 있으면 그걸 쓰고**, 없으면 표시 행 목록에서 계산한다(§7-12).
    // ⚠️ **"접힌 행"과 "스크롤로 벗어난 행"을 구분해야 한다**:
    //   접힌 행은 `layout.rows`(= 표시 행 목록)에 아예 없으므로 `rowIndex`가 null → 그리지 않는다.
    //   이건 지켜야 하는 동작이다(안 보이는 행으로 선을 그으면 B11·B12의 유령 화살표가 재발한다).
    //   스크롤로 벗어난 행은 목록에 **있으므로** 좌표를 계산해 계속 그린다.
    const resolve = (key) => {
      const drawn = boxRectsRef.current.get(key);
      if (drawn) return drawn;

      const row = rowIndex.get(key);
      const box = row?.boxes?.[0];
      if (!box) return null;

      const top = row.top - scrollTop + LANE_BAR_PADDING_Y;
      return {
        x0: x(box.s),
        x1: x(box.e),
        cy: top + Math.max(6, row.height - LANE_BAR_PADDING_Y * 2) / 2
      };
    };

    edges.forEach(({ from, to }) => {
      const source = resolve(from);
      const target = resolve(to);
      if (!source || !target) return;
      if ((source.cy < -80 && target.cy < -80) || (source.cy > height + 80 && target.cy > height + 80)) return;

      drawDependency(ctx, { sx: source.x1, sy: source.cy, tx: target.x0, ty: target.cy });
    });
  }, [edges, rowIndex]);

  // ── 입력 ──────────────────────────────────────────────────────────────────
  // old OnExpand — 작업주문 행을 펼치면 그 아래 공정/BOM 전체가 함께 펼쳐진다(expandAllChild).
  const handleToggleRow = useCallback((row) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      const below = descendantKeys(allRows, row.rowKey, ROW_KEY_SEP);

      if (next.has(row.rowKey)) {
        next.delete(row.rowKey);
        below.forEach(key => next.delete(key));
      } else {
        next.add(row.rowKey);
        below.forEach(key => next.add(key));
      }
      return next;
    });
  }, [allRows]);

  const hitBar = useCallback((row, ts) => {
    const box = row?.boxes?.[0];
    return box && ts != null && ts >= box.s && ts <= box.e ? box : null;
  }, []);

  const handleRowActivate = useCallback((row, { ts }) => {
    setSelectedRowKey(hitBar(row, ts) ? row.rowKey : null);
  }, [hitBar]);

  const handleHover = useCallback((row, { ts, clientX, clientY }) => {
    const box = row ? hitBar(row, ts) : null;

    if (!box) {
      setHoverRowKey(null);
      closeTooltip();
      return;
    }
    if (hoverRowKey === row.rowKey) return;

    closeTooltip();
    setHoverRowKey(row.rowKey);
    hoverTimerRef.current = setTimeout(() => openTooltip(row, clientX, clientY), 150);
  }, [hitBar, hoverRowKey, closeTooltip, openTooltip]);

  const handleWheelZoom = useCallback((delta) => {
    setWidthZoom(prev => {
      const factor = delta < 0 ? WIDTH_ZOOM_STEP : 1 / WIDTH_ZOOM_STEP;
      return Math.min(WIDTH_ZOOM_MAX, Math.max(WIDTH_ZOOM_MIN, prev * factor));
    });
  }, []);

  // ── 조회 트리거 ───────────────────────────────────────────────────────────
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    setPeriodState(prev => ({
      ...prev, startDate, endDate: new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000)
    }));
    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setAllRows([]);
    setEdges([]);
    setBound(null);
    setLoading(false);
    closeTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간)이 바뀌면 값이 준비되는 즉시 자동 조회(§A-9b). 자유 텍스트
  // (woCd/itemCdNm)는 SearchOptsGroupRetrieve 내부 수동 커밋으로만 조회된다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd]);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  return (
    <>
      {/* 화면 루트를 flex column으로 둔다 — 조회조건(FilterContainer) 높이를 자동으로 빼고 남는
          만큼만 간트가 차지한다. 이전에는 간트 래퍼가 `height: calc(100% - 8px)`였는데, flex column
          안에서 그 값은 **조회조건 높이를 빼주지 않아** 합계가 부모를 넘겼다. 그만큼 프레임 하단이
          잘려 **가로 스크롤바가 화면 밖으로 밀려나** thumb를 잡을 수 없었다(2026-08-19 사용자 제보:
          "트랙 클릭은 되는데 드래그만 안 된다" — 남은 조각에만 클릭이 닿았던 것). */}
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {/* `hasIcons={false}` — 표시 배율 묶음을 아래 툴바 줄로 내려서 **우측에 밀어 붙일 것이 없다.**
            안 주면 `FilterContainer`가 **마지막 자식**(`SearchOptsGroupRetrieve`)을 우측 정렬해
            조회조건 줄이 좌우로 갈라진다(`FilterContainer.js:37-42`). `false`면 빈 자식 하나를 뒤에
            덧대므로 조회조건은 전부 왼쪽에 남고 **접기 버튼은 그대로 우측에 렌더된다**. */}
        <FilterContainer hasIcons={false}>
          <VersionSearchGroup
            value={versionState}
            onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
          />
          <PeriodSearchGroup
            value={periodState}
            onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
            useBucket={false}
          />
          <SearchOptsGroupRetrieve
            fieldRefs={{ woCd: woCdRef, itemCdNm: itemCdNmRef }}
            fieldKeys={SEARCH_FIELD_KEYS}
            labels={SEARCH_FIELD_LABELS}
            retrieve={retrieveOrderGantt}
          />
          {/* TODO(MIG-T2): [ordergantt/OrderGantt] 엑셀 내려받기 미이관.
                old/AG Grid 버전: exportDataAsExcel(그리드 표시 컬럼)
                사유: 자체 구현 프레임에는 그리드가 없어 별도 직렬화가 필요 — 사용자 결정으로 보류
                      (reports/gantt-implementation.md §7-1, reports/deferred-features.md) */}
        </FilterContainer>

        {/* ── 툴바 한 줄 — 표시 배율 묶음을 **조회조건보다 한 단 아래**로 내린다(§7-27).
            자원 간트·보정 간트와 **같은 구성**이다 — 조회조건(무엇을 가져올까)과 표시 배율
            (어떻게 볼까)은 성격이 다르고, 한 줄에 섞이면 눈이 두 번 움직인다.
            주문 간트는 좌측에 놓을 조작이 없어 `ml: "auto"` 하나로 우측 정렬한다. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            <GanttViewControls
              t={t}
              headerUnit={headerUnit}
              onHeaderUnitChange={setHeaderUnit}
              widthZoom={widthZoom}
              onWidthZoomChange={setWidthZoom}
              rowHeight={rowHeight}
              onRowHeightChange={setRowHeight}
              /* 이 줄엔 조회조건 접기 버튼이 없어 기본 우측 여백(`mr`)이 끝에 뜨는 틈이 된다. */
              sx={{ mr: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 0", minHeight: 0, mt: 1, border: "1px solid", borderColor: "divider" }}>
          <GanttFrame
            rows={layout.rows}
            totalHeight={layout.totalHeight}
            columns={columns}
            scale={scale}
            ticks={ticks}
            expandedKeys={expandedKeys}
            selectedRowKey={selectedRowKey}
            loading={loading}
            loadingMessage={t("MSG_Loading")}
            drawBackground={handleDrawBackground}
            drawRow={handleDrawRow}
            drawOverlay={handleDrawOverlay}
            onToggleRow={handleToggleRow}
            onRowActivate={handleRowActivate}
            onHoverPoint={handleHover}
            onWheelZoom={handleWheelZoom}
            apiRef={frameApiRef}
          />
        </Box>
      </Box>

      {/* 바 hover 상세(old OnGanttTip) — 항목/내용 2열 정렬은 공용 컴포넌트가 갖는다 */}
      <GanttTooltip tooltip={tooltip} t={t} />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(OrderGantt);
